import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { api, type UiTheme, type User, type UserProfileUpdate } from '../api/client';
import { applyUserTheme, useAuth } from '../auth/AuthContext';
import { openDocumentationPage, type DocumentationSlug } from '../lib/documentation';

type NavItem = { to: string; label: string; adminOnly?: boolean };

const NAV: NavItem[] = [
  { to: '/', label: 'Прогоны' },
  { to: '/exports', label: 'Выгрузки' },
  { to: '/admin/users', label: 'Пользователи', adminOnly: true },
  { to: '/admin/settings', label: 'Администрирование', adminOnly: true },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const mainRef = useRef<HTMLElement | null>(null);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [themeError, setThemeError] = useState<string | null>(null);
  const [themeSaving, setThemeSaving] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const node = mainRef.current;
    if (!node) return;

    const updateScrollTopVisibility = () => {
      setShowScrollTop(node.scrollTop > 320);
    };

    updateScrollTopVisibility();
    node.addEventListener('scroll', updateScrollTopVisibility, { passive: true });
    return () => node.removeEventListener('scroll', updateScrollTopVisibility);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [mobileMenuOpen]);

  const openDocs = (slug: DocumentationSlug) => {
    setDocsError(null);
    openDocumentationPage(slug).catch(() => {
      setDocsError('Не удалось открыть документацию');
    });
  };

  const scrollMainToTop = () => {
    mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    closeMobileMenu();
    logout();
    navigate('/login', { replace: true });
  };

  const buildProfileBody = (nextTheme: UiTheme): UserProfileUpdate | null => {
    if (!user) return null;
    return {
      email: user.email,
      telegram_chat_id: user.telegram_chat_id,
      notify_email_enabled: user.notify_email_enabled,
      notify_telegram_enabled: user.notify_telegram_enabled,
      notify_on_success: user.notify_on_success,
      notify_on_failure: user.notify_on_failure,
      persist_table_sorting: user.persist_table_sorting,
      persist_table_filters: user.persist_table_filters,
      show_ui_hints: user.show_ui_hints,
      ui_theme: nextTheme,
    };
  };

  const toggleTheme = async () => {
    if (!user || themeSaving) return;
    const previousTheme = user.ui_theme;
    const nextTheme: UiTheme = previousTheme === 'dark' ? 'light' : 'dark';
    const body = buildProfileBody(nextTheme);
    if (!body) return;
    setThemeSaving(true);
    setThemeError(null);
    applyUserTheme(nextTheme);
    try {
      await api.put<User>('/api/users/me', body);
      await refreshUser();
    } catch {
      applyUserTheme(previousTheme);
      setThemeError('Не удалось сохранить тему');
    } finally {
      setThemeSaving(false);
    }
  };

  const currentTheme: UiTheme = user?.ui_theme ?? 'dark';
  const nextThemeLabel = currentTheme === 'dark' ? 'светлую' : 'тёмную';
  const visibleNavItems = NAV.filter((n) => !n.adminOnly || user?.role === 'admin');
  const renderNavLinks = (onNavigate?: () => void) => (
    <nav className="flex flex-col gap-1">
      {visibleNavItems.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end
          onClick={onNavigate ? () => onNavigate() : undefined}
          className={({ isActive }) =>
            [
              'rounded-md px-3 py-2 text-label transition-colors',
              isActive
                ? 'bg-surface-2 text-text-primary'
                : 'text-text-secondary hover:bg-surface-2 hover:text-text-primary',
            ].join(' ')
          }
        >
          {n.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="flex h-full">
      <aside className="hidden md:flex w-[240px] flex-col bg-panel border-r border-border-default/[0.08] p-4 gap-1">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-2 h-2 rounded-sm bg-accent" />
          <span className="font-strong text-body text-text-primary">MP Trend</span>
        </div>
        {renderNavLinks()}
        <div className="mt-auto pt-4 border-t border-border-default/[0.08]">
          <div className="mb-4">
            <div className="rubric mb-2">Тема</div>
            <button
              type="button"
              className="theme-toggle-button"
              aria-label={`Включить ${nextThemeLabel} тему`}
              title={`Включить ${nextThemeLabel} тему`}
              disabled={themeSaving}
              onClick={() => void toggleTheme()}
            >
              <span className="theme-toggle-indicator" aria-hidden="true" />
              <span>{currentTheme === 'dark' ? 'Тёмная' : 'Светлая'}</span>
            </button>
            {themeError ? <div className="mt-2 text-caption text-danger">{themeError}</div> : null}
          </div>
          <div className="mb-4">
            <div className="rubric mb-2">Документация</div>
            <button
              type="button"
              className="btn-ghost btn-sm w-full justify-start"
              onClick={() => openDocs('user')}
            >
              Справка
            </button>
            {user?.role === 'admin' ? (
              <button
                type="button"
                className="btn-ghost btn-sm mt-1 w-full justify-start"
                onClick={() => openDocs('developers')}
              >
                Разработчикам
              </button>
            ) : null}
            {docsError ? <div className="mt-2 text-caption text-danger">{docsError}</div> : null}
          </div>
          <div className="rubric mb-1">Пользователь</div>
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              [
                'block max-w-full truncate text-label transition-colors',
                isActive ? 'text-accent' : 'text-text-primary hover:text-accent',
              ].join(' ')
            }
            title="Открыть профиль"
          >
            {user?.username}
          </NavLink>
          <button className="btn-ghost btn-sm w-full justify-start mt-3" onClick={handleLogout}>
            Выйти
          </button>
        </div>
      </aside>

      <main ref={mainRef} className="flex-1 overflow-auto">
        <div className="mobile-topbar">
          <button
            type="button"
            className="mobile-menu-button"
            aria-label="Открыть меню"
            aria-controls="mobile-navigation-drawer"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(true)}
          >
            <span aria-hidden="true">☰</span>
            <span>Меню</span>
          </button>
          <span className="font-strong text-body text-text-primary">MP Trend</span>
        </div>
        <div className="max-w-[1440px] mx-auto p-6 pt-4 md:p-8">{children}</div>
      </main>

      {mobileMenuOpen ? (
        <div className="mobile-menu-overlay" role="dialog" aria-modal="true" aria-label="Мобильное меню">
          <button
            type="button"
            className="mobile-menu-backdrop"
            aria-label="Закрыть меню"
            onClick={closeMobileMenu}
          />
          <aside id="mobile-navigation-drawer" className="mobile-menu-panel" aria-label="Мобильная навигация">
            <div className="mb-6 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-sm bg-accent" />
                <span className="font-strong text-body text-text-primary">MP Trend</span>
              </div>
              <button
                type="button"
                className="btn-ghost btn-sm h-8 w-8 px-0"
                aria-label="Закрыть меню"
                title="Закрыть меню"
                onClick={closeMobileMenu}
              >
                ×
              </button>
            </div>
            {renderNavLinks(closeMobileMenu)}
            <div className="mt-auto pt-4 border-t border-border-default/[0.08]">
              <div className="mb-4">
                <div className="rubric mb-2">Тема</div>
                <button
                  type="button"
                  className="theme-toggle-button"
                  aria-label={`Включить ${nextThemeLabel} тему`}
                  title={`Включить ${nextThemeLabel} тему`}
                  disabled={themeSaving}
                  onClick={() => void toggleTheme()}
                >
                  <span className="theme-toggle-indicator" aria-hidden="true" />
                  <span>{currentTheme === 'dark' ? 'Тёмная' : 'Светлая'}</span>
                </button>
                {themeError ? <div className="mt-2 text-caption text-danger">{themeError}</div> : null}
              </div>
              <div className="mb-4">
                <div className="rubric mb-2">Документация</div>
                <button
                  type="button"
                  className="btn-ghost btn-sm w-full justify-start"
                  onClick={() => openDocs('user')}
                >
                  Справка
                </button>
                {user?.role === 'admin' ? (
                  <button
                    type="button"
                    className="btn-ghost btn-sm mt-1 w-full justify-start"
                    onClick={() => openDocs('developers')}
                  >
                    Разработчикам
                  </button>
                ) : null}
                {docsError ? <div className="mt-2 text-caption text-danger">{docsError}</div> : null}
              </div>
              <div className="rubric mb-1">Пользователь</div>
              <NavLink
                to="/profile"
                onClick={closeMobileMenu}
                className={({ isActive }) =>
                  [
                    'block max-w-full truncate text-label transition-colors',
                    isActive ? 'text-accent' : 'text-text-primary hover:text-accent',
                  ].join(' ')
                }
                title="Открыть профиль"
              >
                {user?.username}
              </NavLink>
              <button className="btn-ghost btn-sm w-full justify-start mt-3" onClick={handleLogout}>
                Выйти
              </button>
            </div>
          </aside>
        </div>
      ) : null}

      {showScrollTop ? (
        <button
          type="button"
          className="scroll-top-button"
          aria-label="Вернуться наверх"
          title="Наверх"
          onClick={scrollMainToTop}
        >
          ↑
        </button>
      ) : null}
    </div>
  );
}